import { Card, CardSection, Button } from '@trafiflow/shared-ui'
import type { Agent } from '../../../services/agentplat/types'
import { User, X, Gift, DollarSign, AlertCircle } from 'lucide-react'

interface AgentContractModalProps {
  agent: Agent
  isOpen: boolean
  onClose: () => void
  onAccept: () => void
  isLoading?: boolean
}

export default function AgentContractModal({
  agent,
  isOpen,
  onClose,
  onAccept,
  isLoading = false,
}: AgentContractModalProps) {
  if (!isOpen) return null

  const formatPrice = (price?: number) => {
    if (price === undefined || price === null) return 'Not available'
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(price)
  }

  const formatPricePerUnit = (price: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 4,
      maximumFractionDigits: 4,
    }).format(price)
  }

  return (
    <>
      {/* Overlay */}
      <div
        className="fixed inset-0 bg-black/50 z-40"
        onClick={onClose}
      />
      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-3xl max-h-[90vh] overflow-y-auto">
          <CardSection>
            {/* Header */}
            <div className="flex items-start justify-between mb-6">
              <div className="flex items-start gap-4 flex-1">
                {agent.avatar ? (
                  <img
                    src={agent.avatar}
                    alt={agent.name}
                    className="w-20 h-20 rounded-lg object-cover flex-shrink-0 border border-tf-border"
                  />
                ) : (
                  <div className="w-20 h-20 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0 border border-tf-border">
                    <User className="w-10 h-10 text-primary" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <h2 className="text-2xl font-bold text-tf-text mb-2">
                    {agent.name}
                  </h2>
                  {agent.role && (
                    <div className="mb-2">
                      <span className="inline-flex items-center px-2.5 py-1 rounded-md text-sm font-medium bg-tf-feedback-info-bg text-tf-feedback-info-text">
                        {typeof agent.role === 'string' ? agent.role : agent.role.name}
                      </span>
                    </div>
                  )}
                  <p className="text-xs text-tf-text-muted font-mono mb-2">
                    ID: {agent.id}
                  </p>
                  {agent.status && (
                    <div className="flex items-center gap-2">
                      <span
                        className={`w-2 h-2 rounded-full ${ agent.status === 'online' ? 'bg-tf-success' : 'bg-tf-text-muted' }`}
                      ></span>
                      <span className="text-sm font-medium text-tf-text-muted capitalize">
                        {agent.status}
                      </span>
                    </div>
                  )}
                </div>
              </div>
              <button
                onClick={onClose}
                className="text-tf-text-muted hover:text-tf-text-muted transition-colors"
                aria-label="Close"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Contract Title */}
            <div className="mb-6 pb-4 border-b border-tf-border">
              <h3 className="text-xl font-bold text-tf-text">Agent Contract</h3>
              <p className="text-sm text-tf-text-muted mt-1">
                Review the terms and conditions before accepting
              </p>
            </div>

            {/* Trial Period */}
            {agent.trialPeriodDays !== undefined && agent.trialPeriodDays > 0 && (
              <div className="mb-6 p-4 bg-tf-feedback-success-bg border border-tf-feedback-success-border rounded-lg">
                <div className="flex items-start gap-3">
                  <Gift className="w-5 h-5 text-tf-feedback-success-text flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <h4 className="font-semibold text-tf-feedback-success-text mb-1">
                      Free Trial Period
                    </h4>
                    <p className="text-sm text-tf-feedback-success-text">
                      Enjoy <strong>{agent.trialPeriodDays} days</strong> of free trial
                      before billing begins.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Monthly Price */}
            <div className="mb-6 p-4 bg-tf-feedback-info-bg border border-tf-feedback-info-border rounded-lg">
              <div className="flex items-start gap-3">
                <DollarSign className="w-5 h-5 text-tf-feedback-info-text flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <h4 className="font-semibold text-tf-feedback-info-text mb-1">Monthly Price</h4>
                  <p className="text-2xl font-bold text-tf-feedback-info-text">
                    {formatPrice(agent.price)}
                  </p>
                  {agent.price === undefined || agent.price === null ? (
                    <p className="text-xs text-tf-feedback-info-text mt-1">
                      Price is not available at this time
                    </p>
                  ) : (
                    <p className="text-xs text-tf-feedback-info-text mt-1">
                      Billed monthly after the trial period
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Variable Costs */}
            {agent.variableCosts && agent.variableCosts.length > 0 && (
              <div className="mb-6">
                <h4 className="font-semibold text-tf-text mb-3 flex items-center gap-2">
                  <AlertCircle className="w-5 h-5 text-tf-text-muted" />
                  Variable Costs
                </h4>
                <p className="text-sm text-tf-text-muted mb-4">
                  These additional costs depend on usage and are billed on top of the
                  base monthly price.
                </p>
                <div className="space-y-3">
                  {agent.variableCosts.map((cost, index) => (
                    <div
                      key={index}
                      className="p-4 border border-tf-border rounded-lg bg-tf-bg-muted"
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1">
                          <h5 className="font-semibold text-tf-text">{cost.name}</h5>
                          {cost.description && (
                            <p className="text-sm text-tf-text-muted mt-1">{cost.description}</p>
                          )}
                        </div>
                        <div className="text-right ml-4">
                          <p className="font-semibold text-tf-text">
                            {formatPricePerUnit(cost.pricePerUnit)}
                          </p>
                          <p className="text-xs text-tf-text-muted">per {cost.unit}</p>
                        </div>
                      </div>
                      {cost.includedLimit !== undefined && cost.includedLimit > 0 && (
                        <div className="mt-2 pt-2 border-t border-tf-border">
                          <p className="text-xs text-tf-text-muted">
                            <span className="font-medium">
                              {cost.includedLimit.toLocaleString('en-US')} {cost.unit}
                            </span>{' '}
                            included in the base plan
                          </p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Agent Description */}
            {agent.description && (
              <div className="mb-6">
                <h4 className="font-semibold text-tf-text mb-2">Agent Description</h4>
                <p className="text-sm text-tf-text-muted leading-relaxed">{agent.description}</p>
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-3 pt-4 border-t border-tf-border">
              <Button variant="secondary" onClick={onClose} fullWidth disabled={isLoading}>
                Cancel
              </Button>
              <Button variant="primary" onClick={onAccept} fullWidth disabled={isLoading}>
                {isLoading ? 'Creating Contract...' : 'Accept Contract'}
              </Button>
            </div>
          </CardSection>
        </Card>
      </div>
    </>
  )
}

